import React, {useEffect, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import RadioGroup from "../../ui-components/RadioGroup";
import Button from "../../ui-components/Button";
import Modal from "../../ui-components/Modal";
import useWorksiteTaskPdfGenerator from "../../hooks/useWorksiteTaskPdfGenerator";
import TaskMapEditor from "../WorksiteTask/taskmap/TaskMapEditor";
import {getUserLabel} from "../WorksiteTask/utils";
import _ from "lodash";
import useCountryAxiosInstance from "../../hooks/useCountryAxiosInstance";

const WorksiteTaskExportModal = ({
  worksite,
  worksiteUsers,
  task,
  activities,
  onExit = () => {},
}) => {

  // state
  const [downloading, setDownloading] = useState(false);
  const [exportWithEntities, setExportWithEntities] = useState(false);
  const [taskmap, setTaskmap] = useState(null);
  const [taskmapImage, setTaskmapImage] = useState(null);

  // hooks
  const intl = useIntl();
  const apiRequest = useCountryAxiosInstance();
  const dispatchPDFDownload = useWorksiteTaskPdfGenerator(downloading, setDownloading);

  // utilities
  const isMapLoading = !!task?.id_task_map && !taskmapImage;
  const handlePDFDownload = async () => {
    const data = {
      id: task.id,
      worksite: `${worksite.address} - ${worksite.city}`,
      owner: getUserLabel(worksiteUsers.find((u) => u.id === task.id_owner), intl, false),
      assignee: getUserLabel(worksiteUsers.find((u) => u.id === task.id_assignee), intl, false),
      title: task.des_name,
      subtitle: intl.formatMessage({id: "worksite_task"}) + (!!task.survey?.name ? ` - ${task.survey?.name}` : ""),
      description: task.des_description,
      status: task.status,
      task_map: taskmapImage,
      ..._.pick(task, ['created_at', 'expires_at', 'closed_at', 'flg_approved']),
      activities: activities?.items
        ?.filter((a) => !a.flg_disabled)
        ?.map((a) => ({
          id: a.id,
          title: a.val_text,
          description: a.val_description,
          flg_custom: a.activity_type === 'custom',
          closed_at: a.closed_at,
          closed_by: a.val_closed_by || null,
        }))
      ?? [],
    }

    await dispatchPDFDownload(data, exportWithEntities);
    onExit();  // close modal
  }

  useEffect(() => {
    if (!task?.id_task_map) {
      setTaskmap({})
    }
    else {
      const fetchTaskMapData = async () => {
        const [{image}, {canvas_data}] = await Promise.all([
          apiRequest.get(`/worksites_tasks_maps/all`, {params: {id: task.id_task_map, join: 'image'}})
            .then((res) => res?.[0] ?? {image: null}),
          apiRequest.get(`/worksites_tasks_maps_data/taskid/${task.id}`)
            .then((res) => res ?? {canvas_data: null})
        ]);

        if (!image || !canvas_data)
          throw new Error(`Could not retrieve taskmap info (image: ${!!image}, canvas_data: ${!!canvas_data})`);

        setTaskmap({url: image.val_url, data: canvas_data});
      }

      // noinspection JSIgnoredPromiseFromCall
      fetchTaskMapData();
    }
  }, [task, apiRequest, setTaskmap]);

  return (
    <Modal onExit={onExit}>
      <h2 className="mb-2 text-center text-lg font-extrabold">
        <FormattedMessage id="export_task" />
      </h2>
      <div className="my-4">
        <RadioGroup
          id="rg-task-export-mode"
          options={[
            {
              value: "true",
              name: <span className="whitespace-nowrap"><FormattedMessage id="detailed_report" /></span>,
            },
            {
              value: "false",
              name: <span className="whitespace-nowrap"><FormattedMessage id="synthetic_report" /></span>,
              description: intl.formatMessage({id: 'synthetic_report_helper'})
            },
          ]}
          onChange={(value) => { setExportWithEntities(value === 'true'); }}
          currentValue={exportWithEntities.toString()}
          labelClasses="mb-4"
          horizontal
          wrap={false}
        />
      </div>

      <div className="flex gap-2 items-center">
        <Button
          full
          styleType="empty"
          onClick={onExit}
          containerClassName="flex-1"
          disabled={downloading}
        >
          <FormattedMessage id="cancel" />
        </Button>
        <Button
          full
          onClick={async () => { await handlePDFDownload(); }}
          submitting={downloading || isMapLoading}
          containerClassName="flex-1"
          disabled={isMapLoading}
        >
          <FormattedMessage id="download" />
        </Button>
      </div>

      {/* Hidden task map */}
      {!!taskmap?.data && (
        <div
          className="absolute right-full"  // hidden but not display-none (otherwise the conversion to b64 wouldn't work)
          style={{width: "1000px", aspectRatio: "1"}}
        >
          <TaskMapEditor
            url={taskmap.url}
            editMode={false}
            taskId={task.id}
            taskMapId={task.id_task_map}
            onSave={() => {}}
            initialState={taskmap.data}
            onClickElement={() => {}}
            onLoad={setTaskmapImage}
            zoomOnPin={true}
          />
        </div>
      )}
    </Modal>
  )
}

export default WorksiteTaskExportModal;